import { clsx } from 'clsx';
import { Trash2, Plug, Battery, Snowflake, Flame, Cable, Eye, Monitor, Layers, Construction, Zap, RotateCw } from 'lucide-react';
import { InfrastructureUnit, InfrastructureType, ContainerRack } from '../../types/configurator';
import { INFRASTRUCTURE_COLORS } from '../../data/infrastructure';
import { Button } from '../ui';

interface InfrastructurePanelProps {
  infrastructure: InfrastructureUnit;
  racks: ContainerRack[];
  onUpdate: (updates: Partial<InfrastructureUnit>) => void;
  onRemove: () => void;
  onMountToRack?: (rackId: string) => void;
}

const INFRA_ICONS: Record<InfrastructureType, typeof Plug> = {
  'pdu': Plug,
  'ups': Battery,
  'cooling': Snowflake,
  'fire-suppression': Flame,
  'cable-tray': Cable,
  'monitoring': Eye,
  'workspace': Monitor,
  'insulation': Layers,
  'structural': Construction,
};

const INFRA_LABELS: Record<InfrastructureType, string> = {
  'pdu': 'Power Distribution Unit',
  'ups': 'Uninterruptible Power Supply',
  'cooling': 'Cooling System',
  'fire-suppression': 'Fire Suppression',
  'cable-tray': 'Cable Management',
  'monitoring': 'Monitoring System',
  'workspace': 'Workspace Equipment',
  'insulation': 'Insulation',
  'structural': 'Structural Element',
};

export function InfrastructurePanel({
  infrastructure,
  racks,
  onUpdate,
  onRemove,
  onMountToRack,
}: InfrastructurePanelProps) {
  const Icon = INFRA_ICONS[infrastructure.type];
  const colors = INFRASTRUCTURE_COLORS[infrastructure.type];
  const label = INFRA_LABELS[infrastructure.type];

  const mountedRack = racks.find((r) => r.id === infrastructure.rackId);

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <div className={clsx('flex items-center justify-between border-b p-3', colors.bg)}>
        <div className="flex items-center gap-2">
          <Icon className={clsx('h-5 w-5', colors.text)} />
          <div>
            <input
              type="text"
              value={infrastructure.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              className={clsx(
                'border-none bg-transparent text-sm font-medium focus:outline-none focus:ring-0',
                colors.text
              )}
            />
            <p className="text-[10px] text-gray-500">{label}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-red-500 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Specs */}
      <div className="space-y-2 border-b border-gray-200 p-3">
        <h4 className="text-xs font-medium uppercase text-gray-500">Specifications</h4>

        {/* Power */}
        {infrastructure.specs.powerWatts > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1 text-gray-500">
              <Zap className="h-3 w-3" />
              Power Draw
            </span>
            <span className="font-medium">{infrastructure.specs.powerWatts}W</span>
          </div>
        )}

        {/* PDU-specific */}
        {infrastructure.type === 'pdu' && (
          <>
            {infrastructure.specs.outlets && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Outlets</span>
                <span className="font-medium">{infrastructure.specs.outlets}</span>
              </div>
            )}
            {infrastructure.specs.maxAmps && infrastructure.specs.inputVoltage && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Capacity</span>
                <span className="font-medium">
                  {infrastructure.specs.maxAmps}A @ {infrastructure.specs.inputVoltage}V
                  <span className="ml-1 text-xs text-gray-400">
                    ({infrastructure.specs.maxAmps * infrastructure.specs.inputVoltage}W)
                  </span>
                </span>
              </div>
            )}
          </>
        )}

        {/* UPS-specific */}
        {infrastructure.type === 'ups' && (
          <>
            {infrastructure.specs.capacityVA && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Capacity</span>
                <span className="font-medium">{infrastructure.specs.capacityVA}VA</span>
              </div>
            )}
            {infrastructure.specs.runtimeMinutes && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Runtime (full load)</span>
                <span className="font-medium">{infrastructure.specs.runtimeMinutes} min</span>
              </div>
            )}
          </>
        )}

        {/* Cooling-specific */}
        {infrastructure.type === 'cooling' && (
          <>
            {infrastructure.specs.btuPerHour && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Cooling Capacity</span>
                <span className="font-medium">
                  {infrastructure.specs.btuPerHour.toLocaleString()} BTU/hr
                </span>
              </div>
            )}
            {infrastructure.specs.cfm && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Airflow</span>
                <span className="font-medium">{infrastructure.specs.cfm} CFM</span>
              </div>
            )}
          </>
        )}

        {/* Fire suppression */}
        {infrastructure.type === 'fire-suppression' && infrastructure.specs.coverageAreaM2 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Coverage Area</span>
            <span className="font-medium">{infrastructure.specs.coverageAreaM2} m²</span>
          </div>
        )}

        {/* Dimensions */}
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Size</span>
          <span className="font-medium">
            {infrastructure.specs.widthMm}×{infrastructure.specs.depthMm}mm
            {infrastructure.specs.rackUnits && ` (${infrastructure.specs.rackUnits}U)`}
          </span>
        </div>

        {/* Price */}
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Price</span>
          <span className="font-medium">${infrastructure.specs.price.toLocaleString()}</span>
        </div>
      </div>

      {/* Mounting */}
      <div className="border-b border-gray-200 p-3">
        <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">Mounting</h4>

        {infrastructure.specs.rackUnits ? (
          <div>
            <p className="mb-2 text-xs text-gray-500">
              {mountedRack
                ? `Mounted in ${mountedRack.name}`
                : 'Can be rack-mounted or floor-standing'}
            </p>
            {!mountedRack && onMountToRack && racks.length > 0 && (
              <select
                className="w-full rounded border border-gray-200 px-2 py-1 text-sm"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    onMountToRack(e.target.value);
                  }
                }}
              >
                <option value="">Mount to rack...</option>
                {racks.map((rack) => (
                  <option key={rack.id} value={rack.id}>
                    {rack.name}
                  </option>
                ))}
              </select>
            )}
            {mountedRack && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUpdate({ rackId: undefined, rackPosition: undefined })}
                className="w-full"
              >
                Unmount from rack
              </Button>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-500">Floor-standing unit</p>
        )}
      </div>

      {/* Rotation (for floor-standing items) */}
      {!infrastructure.rackId && (
        <div className="border-b border-gray-200 p-3">
          <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">Rotation</h4>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const currentRotation = infrastructure.rotation || 0;
                const newRotation = ((currentRotation + 90) % 360) as 0 | 90 | 180 | 270;
                onUpdate({ rotation: newRotation });
              }}
              className="flex items-center gap-1"
            >
              <RotateCw className="h-4 w-4" />
              Rotate 90°
            </Button>
            <span className="text-sm text-gray-500">
              {infrastructure.rotation || 0}°
            </span>
          </div>
        </div>
      )}

      {/* Position */}
      <div className="p-3 text-[10px] text-gray-400">
        Position: ({Math.round(infrastructure.position.x)}mm,{' '}
        {Math.round(infrastructure.position.y)}mm)
        {infrastructure.rotation ? ` • ${infrastructure.rotation}°` : ''}
      </div>
    </div>
  );
}
